import { cn } from '../cn';
import { Icon } from './Icon';

import type { IconName } from '../icons';

export type IconSquareTone = 'info' | 'neutral' | 'danger' | 'success' | 'warning';
export type IconSquareSize = 'sm' | 'md' | 'lg';

const TONE: Record<IconSquareTone, { box: string; ink: string }> = {
  info: { box: 'bg-surface-static-info-default', ink: 'var(--surface-static-info-accented)' },
  neutral: { box: 'bg-surface-static-neutral-hover', ink: 'var(--content-subdued)' },
  danger: { box: 'bg-surface-static-danger-default', ink: 'var(--surface-static-danger-accented)' },
  success: { box: 'bg-surface-static-success-default', ink: 'var(--surface-static-success-accented)' },
  warning: { box: 'bg-surface-static-warning-default', ink: 'var(--surface-static-warning-accented)' },
};

/** `sm` 28 px (menu rows), `md` 30 px (tiles and section heads), `lg` 36 px (dialog heads). */
const SIZE: Record<IconSquareSize, string> = {
  sm: 'size-7',
  md: 'size-[30px]',
  lg: 'size-9',
};

export interface IconSquareProps {
  icon: IconName;
  tone?: IconSquareTone | undefined;
  size?: IconSquareSize | undefined;
  /** The glyph's width and height in px. Default 15. */
  iconSize?: number | undefined;
  className?: string | undefined;
}

/** A small tinted square (--radius-md) with one icon in the tone's accent colour. */
export function IconSquare({ icon, tone = 'info', size = 'md', iconSize = 15, className }: IconSquareProps) {
  const t = TONE[tone];
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md',
        SIZE[size],
        t.box,
        className,
      )}
    >
      <Icon name={icon} size={iconSize} color={t.ink} />
    </span>
  );
}
